import React, { useEffect, useRef, useState } from "react";
import { Box, Typography, Container, Button, Modal, IconButton, Tabs, Tab, useMediaQuery } from "@mui/material";
import { gsap } from "gsap";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import HomeIcon from "@mui/icons-material/Home"; // Home Icon
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { backsidebackgrounddesktop, backsidebackgroundmobile, gardientline, newfloor1 } from "../../../assets";

// Floor plan data
const floorPlansData = {
  "west": [
    { "size": "1510", "image": "https://dprstorage.b-cdn.net/kolla/luxorfloorplans/TypicalFloorPlans/New%20floor%20plans-20.png", "details": "3 BHK, 2 Bath, Balcony", "table" : "https://dprstorage.b-cdn.net/kolla/luxorfloorplans/Tables/unit%202%203%204.png" },
    { "size": "1775", "image": "https://dprstorage.b-cdn.net/kolla/luxorfloorplans/TypicalFloorPlans/New%20floor%20plans-19.png", "details": "3 BHK, 3 Bath, Study Room", "table" : "https://dprstorage.b-cdn.net/kolla/luxorfloorplans/Tables/unit%205.png" },
    { "size": "1815", "image": "https://dprstorage.b-cdn.net/kolla/luxorfloorplans/TypicalFloorPlans/New%20floor%20plans-21.png", "details": "4 BHK, 3 Bath, Balcony", "table" : "https://dprstorage.b-cdn.net/kolla/luxorfloorplans/Tables/unit%201.png" }
  ],
  "east": [
    { "size": "1510", "image": "https://dprstorage.b-cdn.net/kolla/luxorfloorplans/TypicalFloorPlans/New%20floor%20plans-17.png", "details": "3 BHK, 2 Bath, Balcony", "table" : "https://dprstorage.b-cdn.net/kolla/luxorfloorplans/Tables/unit%207%2C8%2C9.png" },
    { "size": "1775", "image": "https://dprstorage.b-cdn.net/kolla/luxorfloorplans/TypicalFloorPlans/New%20floor%20plans-18.png", "details": "3 BHK, 3 Bath, Study Room", "table" : "https://dprstorage.b-cdn.net/kolla/luxorfloorplans/Tables/unit%205.png" },
    { "size": "1815", "image": "https://dprstorage.b-cdn.net/kolla/luxorfloorplans/TypicalFloorPlans/New%20floor%20plans-16.png", "details": "4 BHK, 3 Bath, Balcony", "table" : "https://dprstorage.b-cdn.net/kolla/luxorfloorplans/Tables/unit%2010.png" }
  ]
};

// Positions of the home icons on the main floor plan
const homeIcons: { id: number; top: string; left: string; section: "west" | "east"; index: number }[] = [
  { id: 1, top: "30%", left: "12%", section: "west", index: 2 },
  { id: 2, top: "70%", left: "12%", section: "east", index: 2 }, 
  { id: 3, top: "30%", left: "88%", section: "west", index: 1 }, 
  { id: 4, top: "70%", left: "88%", section: "east", index: 1 }, 
  { id: 5, top: "30%", left: "30%", section: "west", index: 0 }, 
  { id: 6, top: "30%", left: "50%", section: "west", index: 0 }, 
  { id: 7, top: "30%", left: "70%", section: "west", index: 0 }, 
  { id: 8, top: "70%", left: "30%", section: "east", index: 0 }, 
  { id: 9, top: "70%", left: "50%", section: "east", index: 0 }, 
  { id: 10, top: "70%", left: "70%", section: "east", index: 0 },
];

const FloorPlans: React.FC = () => {
  const textRef = useRef<HTMLDivElement | null>(null);
  const imageRef = useRef<HTMLDivElement | null>(null);
  const tabContentRef = useRef<HTMLDivElement | null>(null); // Ref for tab content animation
  const [selectedSection, setSelectedSection] = useState<"east" | "west">("east");
  const [selectedPlan, setSelectedPlan] = useState(floorPlansData.east[0]);
  const [openModal, setOpenModal] = useState(false);
  const isMobile = useMediaQuery("(max-width: 768px)");

  useEffect(() => {
    gsap.fromTo(textRef.current, { opacity: 0, y: 30 }, { opacity: 1, y: 0, duration: 1, ease: "power2.out" });
    gsap.fromTo(imageRef.current, { opacity: 0, scale: 0.9 }, { opacity: 1, scale: 1, duration: 1.2, ease: "power2.out", delay: 0.3 });
  }, []);

  const handleTabChange = (newValue: "east" | "west") => {
    if (tabContentRef.current) {
      // Animate out the current content
      gsap.to(tabContentRef.current, {
        opacity: 0,
        y: 30,
        duration: 0.5,
        ease: "power2.out",
        onComplete: () => {
          // Update the state after the animation completes
          setSelectedSection(newValue);
          setSelectedPlan(floorPlansData[newValue][0]);

          // Animate in the new content
          gsap.fromTo(
            tabContentRef.current,
            { opacity: 0, y: 30 },
            { opacity: 1, y: 0, duration: 0.5, ease: "power2.out" }
          );
        },
      });
    }
  };

  const handlePlanChange = (plan: typeof floorPlansData["east"][0]) => {
    if (imageRef.current) {
      // Animate out the current image
      gsap.to(imageRef.current, {
        opacity: 0,
        scale: 0.95,
        duration: 0.6,
        ease: "power2.inOut",
        onComplete: () => {
          // Update the state after the animation completes
          setSelectedPlan(plan);

          // Animate in the new image
          gsap.fromTo(
            imageRef.current,
            { opacity: 0, scale: 0.95 },
            { opacity: 1, scale: 1, duration: 0.6, ease: "power2.inOut" }
          );
        },
      });
    }
  };

  const handleDownload = () => {
    const link = document.createElement("a");
    link.href = selectedPlan.image;
    link.download = `FloorPlan-${selectedPlan.size}.jpg`;
    link.click();
  };

  // Function to update floor plan selection based on home icon click
  const handleHomeIconClick = (section: "east" | "west", index: number) => {
    setSelectedSection(section);
    handlePlanChange(floorPlansData[section][index]);
  };

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height: isMobile ? "auto" : "100vh",
        backgroundImage: isMobile ? `url(${backsidebackgroundmobile})` : `url(${backsidebackgrounddesktop})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
        px: { xs: 2, md: 5 },
      }}
    >
      <Box
        sx={{
          position: "relative",
          width: "100%",
          color: "white",
          display: "flex",
          flexDirection: "column",
          py: { xs: 1, md: 4 },
          px: { xs: 0, md: 2 },
        }}
      >
        <Container
          maxWidth="xl"
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
            height: "20vh",
          }}
        >
          {/* Title */}
          <Box
            sx={{
              display: "flex",
              width: "100%",
              justifyContent: "center",
              alignItems: "center",
              flexDirection: "column",
              mt: 3,
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontWeight: "600",
                fontSize: "28px",
                background: "linear-gradient(90deg, #bf742c, #fff8da, #c58038)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                display: "inline-block",
                mt: 2,
              }}
            >
              Typical Floor Layout (2-12)
            </Typography>
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", my: 1 }}>
              <img src={gardientline} alt="2.5 BHK luxury apartment in Hyderabad by Kolla Group" style={{ width: "100%", maxWidth: "700px" }} />
            </Box>
          </Box>

          {/* Tabs Section */}
          <Box sx={{ display: "flex", justifyContent: "center", mb: 0.5, pb: 1 }}>
            <Tabs
              value={selectedSection}
              onChange={(event, newValue: "east" | "west") => handleTabChange(newValue)}
              textColor="inherit"
              indicatorColor="primary"
              centered
            >
              {["east", "west"].map((section) => (
                <Tab
                  key={section}
                  label={
                    <Typography
                      variant="h2"
                      sx={{ fontSize: 16, fontWeight: selectedSection === section ? "600" : "400" }}
                    >
                      {section}
                    </Typography>
                  }
                  value={section}
                  sx={{
                    textTransform: "uppercase",
                    fontWeight: selectedSection === section ? "bold" : "normal",
                    borderBottom: selectedSection === section ? "1.5px solid #c58038" : "none",
                    background: selectedSection === section
                      ? "linear-gradient(90deg, #bf742c, #fff8da, #c58038)"
                      : "none",
                    WebkitBackgroundClip: selectedSection === section ? "text" : "none",
                    WebkitTextFillColor: selectedSection === section ? "transparent" : "inherit",
                    "&.Mui-selected": { borderBottom: "1.5px solid #c58038", marginBottom: -1.5 },
                    "&:not(.Mui-selected)": { marginBottom: -1.5 },
                  }}
                />
              ))}
            </Tabs>
          </Box>

          {/* Animated Content */}
          <Box ref={tabContentRef}>
            <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
              {floorPlansData[selectedSection].map((plan) => (
                <Button
                  key={plan.size}
                  variant="contained"
                  sx={{
                    background: selectedPlan.size === plan.size
                      ? "linear-gradient(90deg, #bf742c, #fff8da, #c58038)"
                      : "black",
                    color: selectedPlan.size === plan.size ? "black" : "white",
                    fontSize: selectedPlan.size === plan.size ? 18 : 14,
                    fontWeight: "bold",
                    px: isMobile ? 2 : 4,
                    py: 0.5,
                    border: "2px solid rgb(146, 146, 146)",
                    borderRadius: 0,
                    "&:hover": {
                      background: "linear-gradient(90deg, #bf742c, #fff8da, #c58038)",
                      color: "black",
                    },
                    transition: "all 0.3s ease-in-out",
                    marginLeft: "-2px", // To remove the gap between buttons
                  }}
                  onClick={() => handlePlanChange(plan)}
                >
                  <Box
                    component="span"
                    sx={{ fontSize: selectedPlan.size === plan.size ? 20 : 16, display: "block" }}
                  >
                    <Typography
                      variant="h2"
                      sx={{ fontSize: isMobile ? 16 : 18, fontWeight: "600", mr: 0.5 }}
                    >
                      {plan.size + " "}
                    </Typography>
                  </Box>
                  <Box
                    component="span"
                    sx={{ fontSize: selectedPlan.size === plan.size ? 10 : 10, display: "block", mt: "6px" }}
                  >
                    {" SQ. FT"}
                  </Box>
                </Button>
              ))}
            </Box>
          </Box>
        </Container>

        <Container maxWidth="xl" sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, justifyContent: "center", alignItems: "center", width: "100%", height: "auto", mt: 3}}>
        
          {/* border as linear gradient as (linear-gradient(90deg, #bf742c, #fff8da, #c58038)) */}
          <Box ref={imageRef} sx={{ textAlign: "center", position: "relative", width: "100%", maxWidth: "480px", height: "auto", aspectRatio: "1 / 1", mt: 4 }}>
            <img src={selectedPlan.image} alt="2.5 BHK luxury apartment in Hyderabad by Kolla Group" style={{ width: "100%", height: "100%", objectFit: "contain", borderRadius: 8, cursor: "pointer", border: "1px solid #c58038", padding: "5px", boxShadow: "0 0 10px rgba(0,0,0,0.5)" }} onClick={() => setOpenModal(true)} />
          </Box>

          
          <Box
            sx={{
              position: "relative",
              textAlign: "center",
              mt: { xs: 3, md: 0 },
              mb: { xs: 5, md: 0 },
              width: isMobile ? "100%" : "35%",
              transform: isMobile ? "" : "rotate(90deg)", 
              transformOrigin: "center", // Set the rotation origin to the center
            }}
          >
            <img
              src="https://dprstorage.b-cdn.net/kolla/First-Floor-PlateFinal.png"
              alt="2.5 BHK luxury apartment in Hyderabad by Kolla Group"
              style={{ width: "100%", maxWidth: "550px", borderRadius: 8 }}
            />
            {homeIcons.map((icon) => (
              <IconButton
                key={icon.id}
                onClick={() => handleHomeIconClick(icon.section, icon.index)}
                sx={{
                position: "absolute",
                top: icon.top,
                left: icon.left,
                transform: "translate(-50%, -50%)",
                backgroundColor:
                  selectedSection === icon.section &&
                  selectedPlan.size === floorPlansData[icon.section][icon.index].size
                  ? "#bf742c"
                  : "rgba(197, 128, 56, 0.5)",
                color: "white",
                borderRadius: "50%",
                width: 30,
                height: 30,
                "&:hover": { backgroundColor: "#bf742c" },
                }}
              >
                <HomeIcon sx={{ fontSize: 20 }} />
              </IconButton>
            ))}
          </Box>
        </Container>

        {/* Full Screen Modal */}
        <Modal open={openModal} onClose={() => setOpenModal(false)}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              height: "100vh",
              backgroundColor: "rgba(0,0,0,0.8)",
              overflowY: "auto",
            }}
          >
            <IconButton
              sx={{ position: "absolute", top: 20, right: 20, color: "white" }}
              onClick={() => setOpenModal(false)}
            >
              <CloseIcon sx={{ fontSize: 40 }} />
            </IconButton>
            <IconButton
              sx={{ position: "absolute", top: 20, left: 20, color: "white" }}
              onClick={handleDownload}
            >
              <DownloadIcon sx={{ fontSize: 40 }} />
            </IconButton>
            <IconButton
              sx={{
                position: "absolute",
                top: "50%",
                left: 20,
                color: "white",
                transform: "translateY(-50%)",
              }}
              onClick={() =>
                handlePlanChange(
                  floorPlansData[selectedSection][
                    (floorPlansData[selectedSection].indexOf(selectedPlan) -
                      1 +
                      floorPlansData[selectedSection].length) %
                      floorPlansData[selectedSection].length
                  ]
                )
              }
            >
              <ArrowBackIosIcon sx={{ fontSize: 40 }} />
            </IconButton>
            <IconButton
              sx={{
                position: "absolute",
                top: "50%",
                right: 20,
                color: "white",
                transform: "translateY(-50%)",
              }}
              onClick={() =>
                handlePlanChange(
                  floorPlansData[selectedSection][
                    (floorPlansData[selectedSection].indexOf(selectedPlan) +
                      1) %
                      floorPlansData[selectedSection].length
                  ]
                )
              }
            >
              <ArrowForwardIosIcon sx={{ fontSize: 40 }} />
            </IconButton>
            <Box
              component="img"
              src={selectedPlan.image}
              alt="2.5 BHK luxury apartment in Hyderabad by Kolla Group"
              sx={{ maxWidth: "90%", maxHeight: "60%", mb: 2 }}
            />
            <Box
              component="img"
              src={selectedPlan.table}
              alt="2.5 BHK luxury apartment in Hyderabad by Kolla Group"
              sx={{ maxWidth: "90%", maxHeight: "30%" }}
            />
          </Box>
        </Modal>
      </Box>
    </Box>
  );
};

export default FloorPlans;